"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Award, CircleCheck as CheckCircle2, Clock, PhoneCall, ShieldCheck, Star, Wrench, CircleQuestionMark as HelpCircle, Sparkles } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const trustBadges = [{
  icon: ShieldCheck,
  title: "Certified Kohler Specialists",
  description: "Trained on OEM Kohler parts, toilet valves, and touchless fixtures."
}, {
  icon: Award,
  title: "15+ Years Experience",
  description: "Decades serving Jonesboro homes with precision Kohler installations."
}, {
  icon: Clock,
  title: "24/7 Rapid Response",
  description: "Emergency repairs for overflowing Kohler toilets and broken cartridges."
}, {
  icon: CheckCircle2,
  title: "100% Genuine OEM Parts",
  description: "Direct-fit Kohler canisters, flappers, and ceramic disc cartridges in stock."
}];
const faqs = [{
  id: "item-1",
  question: "Do you use genuine Kohler OEM replacement parts?",
  answer: "Yes. We stock authentic Kohler replacement parts including genuine Kohler canister seals, Class Five flush tower valves, Rite-Temp shower cartridges, and touchless sensor solenoids to protect your product warranty and ensure factory-grade performance."
}, {
  id: "item-2",
  question: "Why does my Kohler toilet keep running or ghost-flushing?",
  answer: "Most running Kohler toilets are caused by a worn canister flush seal (the yellow or red silicone ring) or an improperly seated fill valve. Our technicians carry exact Kohler-fit seals and can diagnose and rebuild the assembly in one visit."
}, {
  id: "item-3",
  question: "Can you fix leaking Kohler Rite-Temp shower valves?",
  answer: "Absolutely. Kohler Rite-Temp mixing valves rely on specific pressure-balancing cartridges (GP77759 / GP800820). We replace worn ceramic discs, flush out mineral sediment, and restore smooth temperature control without opening your wall tiles."
}, {
  id: "item-4",
  question: "How fast can you respond for Kohler emergencies in Jonesboro, GA?",
  answer: "Our emergency plumbing fleet is stationed locally in Jonesboro, GA. For urgent leaks, main shutoff emergencies, or flooded Kohler fixtures, we typically arrive on-site within 45 to 60 minutes, 24 hours a day."
}, {
  id: "item-5",
  question: "Do you install customer-supplied Kohler fixtures and sinks?",
  answer: "Yes. Whether you bought a Kohler Cimarron toilet, farmhouse kitchen sink, or Kohler smart bidet seat, our licensed team ensures precise alignment, code-compliant drainage, and leak-free water supply hookups."
}];
const testimonials = [{
  quote: "Our Kohler dual-flush toilet was leaking into the basement ceiling. Jonesboro Emergency Plumber showed up at 10 PM, had the exact Kohler valve in the truck, and stopped the leak immediately.",
  author: "Marcus T.",
  location: "Jonesboro, GA",
  service: "Kohler Class Five Rebuild",
  rating: 5,
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/customer_review_marcus.webp",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/customer_review_marcus.webp-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/customer_review_marcus.webp-wvc-sizes",
  avatarFallback: "MT"
}, {
  quote: "Extremely knowledgeable on Kohler fixtures. Replaced our master bath Rite-Temp cartridge and restored full hot water pressure in under an hour. Clean, polite, and very reasonable pricing.",
  author: "Elena R.",
  location: "Clayton County, GA",
  service: "Kohler Shower Cartridge",
  rating: 5,
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/customer_review_elena.webp",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/customer_review_elena.webp-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/customer_review_elena.webp-wvc-sizes",
  avatarFallback: "ER"
}];
export default function KohlerFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="kohlerfaqandtrust" data-nav="light" className="relative bg-background py-16 sm:py-24 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-semibold text-xs border-primary/20 bg-secondary/50">
            <Sparkles className="size-3 text-accent mr-1" />
            Kohler Certified Standards & Trust
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Kohler Fixture Expertise & Common Questions
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Get clear, honest answers regarding genuine Kohler parts, valve rebuilds, and emergency fixture repairs in Jonesboro, GA.
          </p>
        </div>

        {/* Trust Badges Grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-16 sm:mb-20">
          {trustBadges.map((badge, index) => {
          const Icon = badge.icon;
          return <motion.div key={badge.title} data-index={index} variants={itemVariants}>
                <Card className="h-full border border-border bg-card shadow-xs hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-base sm:text-lg font-bold font-default">
                      {badge.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {badge.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Main Content Layout: FAQs on Left, Testimonials & CTA on Right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* FAQ Column (7 cols) */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-6">
              <HelpCircle className="size-5 text-primary" />
              <h3 className="text-xl sm:text-2xl font-bold font-default text-foreground">
                Frequently Asked Questions
              </h3>
            </div>

            <Card className="border border-border bg-card p-2 sm:p-4 shadow-sm">
              <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
                {faqs.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border-border px-2">
                    <AccordionTrigger className="text-left font-default font-semibold text-sm sm:text-base py-4 text-foreground hover:no-underline hover:text-primary">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </Card>

            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs sm:text-sm text-muted-foreground">
              <span>Looking for specific toilet or faucet service?</span>
              <Link to="/toilet-repair/" className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80">
                Toilet Repair
              </Link>
              <span aria-hidden="true">•</span>
              <Link to="/faucet-sink-repair/" className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80">
                Faucet & Sink Repair
              </Link>
            </div>
          </div>

          {/* Testimonials & Trust Sidebar (5 cols) */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="flex items-center gap-2 mb-1">
              <Star className="size-5 fill-accent text-accent" />
              <h3 className="text-xl sm:text-2xl font-bold font-default text-foreground">
                Verified Local Experiences
              </h3>
            </div>

            {testimonials.map((test, index) => <Card key={test.author} data-index={index} className="border border-border bg-card shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {Array.from({
                    length: test.rating
                  }).map((_, starIndex) => <Star key={starIndex} data-index={starIndex} className="size-4 fill-accent text-accent" />)}
                    </div>
                    <Badge variant="secondary" className="text-[11px] font-medium">
                      {test.service}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pb-4">
                  <p className="text-sm text-card-foreground leading-relaxed italic">
                    "{test.quote}"
                  </p>
                </CardContent>
                <CardHeader className="pt-0 pb-4">
                  <div className="flex items-center gap-3">
                    <SafeHydrate><Avatar className="size-10 border border-border">
                      <AvatarImage src={test.avatarSrc} data-wvc-srcset={test.avatarSrcset} data-wvc-sizes={test.avatarSizes} alt={test.author} loading="lazy" decoding="async" />
                      <AvatarFallback className="font-semibold text-xs bg-muted text-muted-foreground">
                        {test.avatarFallback}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div>
                      <CardTitle className="text-sm font-semibold text-foreground">
                        {test.author}
                      </CardTitle>
                      <CardDescription className="text-xs text-muted-foreground">
                        {test.location}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>)}

            {/* Emergency CTA Card */}
            <Card className="border border-primary/20 bg-primary text-primary-foreground shadow-md">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Wrench className="size-5 text-accent" />
                  <h4 className="font-default font-bold text-lg text-primary-foreground">
                    Urgent Kohler Problem?
                  </h4>
                </div>
                <p className="text-xs sm:text-sm text-primary-foreground/90 leading-relaxed mb-5">
                  We carry common Kohler replacement parts in our service vans for fast, 24/7 same-day fixes.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="bg-accent text-accent-foreground font-semibold hover:bg-accent/90">
                    <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      <span>Call (470) 847-9076</span>
                    </a>
                  </Button>
                  <Button asChild variant="secondary" size="lg" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-semibold">
                    <Link to="/contact/">Request Service</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}